/* ============================================================================
 * branding.js — white-label proposal branding + tier gating.
 *
 * The company can customize how the client-facing proposal header looks:
 *   - saying          (the tagline / slogan; stored as company.tagline)
 *   - logo            (company.logo_url — uploaded separately)
 *   - accent_color    (drives the proposal's --primary accent)
 *   - header_font     (font family of the brand name + section eyebrows)
 *   - logo_placement  (left | center | right in the header)
 *
 * Availability is gated by the account tier (migration 010):
 *   tradesman → logo, saying
 *   foreman   → + accent color, font
 *   boss      → + logo placement (full control)
 *
 * NOTE: tier gating here is a UX/product boundary, not a security one. It
 * controls what the settings UI lets you EDIT. The proposal RENDER applies
 * whatever branding is saved (so a value set on a higher tier keeps working).
 * ========================================================================== */

/* Tier ordering — higher index = more access. */
export const TIER_ORDER = ['tradesman', 'foreman', 'boss'];

export const TIER_LABEL = {
  tradesman: 'Tradesman',
  foreman: 'Foreman',
  boss: 'Boss',
};

const tierRank = (tier) => {
  const i = TIER_ORDER.indexOf(tier);
  return i === -1 ? 0 : i;
};

/* Minimum tier required for each branding feature. */
export const BRANDING_FEATURE_TIER = {
  saying: 'tradesman',
  logo: 'tradesman',
  accent_color: 'foreman',
  header_font: 'foreman',
  logo_placement: 'boss',
};

/** True if `tier` unlocks `feature`. */
export const tierAllows = (tier, feature) => {
  const required = BRANDING_FEATURE_TIER[feature] || 'boss';
  return tierRank(tier) >= tierRank(required);
};

/** The tier a feature needs (for "upgrade to X" hints). */
export const requiredTierFor = (feature) =>
  BRANDING_FEATURE_TIER[feature] || 'boss';

/* Default branding block — nested under company_profile. Empty/neutral
 * values mean "use the app defaults" so the proposal is unchanged until the
 * company opts in. */
export const DEFAULT_BRANDING = {
  accent_color: '', // hex like '#b8860b'; '' = keep the app's gold --primary
  header_font: 'brand', // 'brand' | 'sans' | 'serif'
  logo_placement: 'left', // 'left' | 'center' | 'right'
};

/* Font choices — web-safe (no extra font loading). 'brand' keeps the app's
 * Space Grotesk display face via the existing .font-brand class. */
export const HEADER_FONT_OPTIONS = [
  { value: 'brand', label: 'Brand (Space Grotesk)', fontFamily: null },
  {
    value: 'sans',
    label: 'Modern Sans',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  {
    value: 'serif',
    label: 'Classic Serif',
    fontFamily: "Georgia, 'Times New Roman', 'Iowan Old Style', serif",
  },
];

/* A small palette of on-brand accent presets (in addition to the free color
 * input). Values are hex. */
export const ACCENT_PRESETS = [
  { label: 'K.O. Gold', hex: '#b8860b' },
  { label: 'Slate Blue', hex: '#3b5bdb' },
  { label: 'Forest', hex: '#2f9e44' },
  { label: 'Crimson', hex: '#c92a2a' },
  { label: 'Graphite', hex: '#495057' },
  { label: 'Copper', hex: '#e8590c' },
];

export const LOGO_PLACEMENT_OPTIONS = [
  { value: 'left', label: 'Left of name' },
  { value: 'center', label: 'Centered above' },
  { value: 'right', label: 'Right of name' },
];

/* ----------------------------------------------------------------------------
 * Color conversion — hex → "H S% L%" triplet for CSS `hsl(var(--primary))`.
 * The proposal markup already uses hsl(var(--primary)) everywhere, so
 * overriding --primary on the proposal container recolors it wholesale.
 * ------------------------------------------------------------------------- */
export const hexToHslTriplet = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;

  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let s = 0;
  let hue = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hue = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        hue = (b - r) / d + 2;
        break;
      default:
        hue = (r - g) / d + 4;
        break;
    }
    hue /= 6;
  }

  const H = Math.round(hue * 360);
  const S = Math.round(s * 100);
  const L = Math.round(l * 100);
  return `${H} ${S}% ${L}%`;
};

/**
 * Resolve saved branding into render primitives.
 *
 * Returns:
 *   containerStyle  — inline style for the proposal root (accent override)
 *   brandFontFamily — fontFamily string for the brand name, or null (use .font-brand)
 *   useBrandClass   — whether to keep the .font-brand class
 *   logoPlacement   — 'left' | 'center' | 'right'
 */
export const resolveBranding = (branding) => {
  const b = { ...DEFAULT_BRANDING, ...(branding || {}) };

  const triplet = hexToHslTriplet(b.accent_color);
  const containerStyle = triplet ? { '--primary': triplet } : undefined;

  const fontOpt =
    HEADER_FONT_OPTIONS.find((f) => f.value === b.header_font) ||
    HEADER_FONT_OPTIONS[0];

  return {
    containerStyle,
    brandFontFamily: fontOpt.fontFamily, // null for 'brand'
    useBrandClass: fontOpt.value === 'brand',
    logoPlacement: ['left', 'center', 'right'].includes(b.logo_placement)
      ? b.logo_placement
      : 'left',
  };
};
